import { useState } from "react";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";

const FIELDS = [
  { k: "name", label: "Full name", req: true, type: "text", auto: "name" },
  { k: "company", label: "Company", req: true, type: "text", auto: "organization" },
  { k: "product", label: "Product / part required", req: true, type: "text" },
  { k: "quantity", label: "Quantity (e.g. 10,000 pcs/month)", req: true, type: "text" },
  { k: "phone", label: "Phone / WhatsApp", req: true, type: "tel", auto: "tel" },
  { k: "email", label: "Work email", req: true, type: "email", auto: "email" },
];

const Field = ({ f, value, onChange, dark }) => (
  <label className="group block">
    <span className={`eyebrow !text-[10.5px] !tracking-[0.18em] ${dark ? "text-slate-300" : "text-slate-500"}`}>
      {f.label}{f.req && <span className="text-sky-500"> *</span>}
    </span>
    <input
      data-testid={`rfq-input-${f.k}`}
      type={f.type}
      required={f.req}
      autoComplete={f.auto}
      value={value}
      onChange={(e) => onChange(f.k, e.target.value)}
      className={`mt-2 w-full border-0 border-b bg-transparent px-0 py-2.5 text-[15px] outline-none transition-colors focus:ring-0 ${
        dark ? "border-slate-600 text-white placeholder:text-slate-500 focus:border-sky-400" : "border-slate-300 text-slate-900 focus:border-[#0a6cff]"
      }`}
    />
  </label>
);

export const RfqForm = ({ defaultProduct = "", productId = "", defaults = {}, dark = false, onDone }) => {
  const [form, setForm] = useState({ name: "", company: "", product: defaultProduct, quantity: defaults.quantity || "", phone: "", email: "", message: defaults.message || "" });
  const [state, setState] = useState({ loading: false, error: "", ref: "" });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", ref: "" });
    try {
      const { data } = await api.post("/inquiries", { ...form, product_id: productId });
      setState({ loading: false, error: "", ref: data?.reference || "" });
      onDone?.();
    } catch (err) {
      setState({ loading: false, error: formatErr(err), ref: "" });
    }
  };

  if (state.ref) {
    return (
      <div data-testid="rfq-success" className="py-10">
        <CheckCircle2 className="h-12 w-12 text-sky-500" strokeWidth={1.5} />
        <h3 className={`font-display mt-5 text-4xl font-extrabold uppercase ${dark ? "text-white" : "text-slate-950"}`}>RFQ received</h3>
        <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>
          Thank you, {form.name.split(" ")[0]}. Our sales team will get back to you shortly. Your reference number is
        </p>
        <p data-testid="rfq-reference" className="font-mono-x mt-3 inline-block bg-[#0a6cff] px-3 py-1.5 text-sm font-semibold tracking-widest text-white">
          #{state.ref}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="rfq-form" className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
      {FIELDS.map((f) => (
        <Field key={f.k} f={f} value={form[f.k]} onChange={set} dark={dark} />
      ))}
      <label className="block sm:col-span-2">
        <span className={`eyebrow !text-[10.5px] !tracking-[0.18em] ${dark ? "text-slate-300" : "text-slate-500"}`}>Specs / drawing notes (optional)</span>
        <textarea
          data-testid="rfq-input-message"
          rows={defaults.message ? 5 : 3}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          className={`mt-2 w-full resize-none border-0 border-b bg-transparent px-0 py-2.5 text-[15px] outline-none focus:ring-0 ${
            dark ? "border-slate-600 text-white focus:border-sky-400" : "border-slate-300 text-slate-900 focus:border-[#0a6cff]"
          }`}
        />
      </label>
      {state.error && (
        <p data-testid="rfq-error" className="sm:col-span-2 text-sm text-red-500">{state.error}</p>
      )}
      <div className="sm:col-span-2 flex flex-wrap items-center gap-4 pt-2">
        <button data-testid="rfq-submit-button" type="submit" disabled={state.loading} className="btn-electric min-w-[200px] disabled:opacity-60">
          {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send RFQ <ArrowRight className="h-4 w-4" /></>}
        </button>
        <span className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>No spam. Your details go only to our sales team.</span>
      </div>
    </form>
  );
};
